<template>
  <div>
    <div class="container-title">
      {{ $t('trips.singular') }}
    </div>
    <b-card no-body>
      <b-card-body>
        <div class="row">
          <div class="col-12 col-md-6 col-xxl-4">
            <div class="row py-1">
              <div class="col-6 col-xxl-4 custom-label">
                {{ lodash.upperFirst($t('sale_notes.shows.date')) }}
              </div>
              <div class="col-1 custom-label">
                :
              </div>
              <div class="col-5 col-xxl-7 custom-value">
                {{ trippedAt }}
              </div>
            </div>
            <div class="row py-1">
              <div class="col-6 col-xxl-4 custom-label">
                {{ lodash.upperFirst($t('sale_notes.partner')) }}
              </div>
              <div class="col-1 custom-label">
                :
              </div>
              <div class="col-5 col-xxl-7 custom-value">
                {{ partnerNames }}
              </div>
            </div>
            <div class="row py-1">
              <div class="col-6 col-xxl-4 custom-label">
                {{ lodash.upperFirst($t('shipping_costs.to_factory')) }}
              </div>
              <div class="col-1 custom-label">
                :
              </div>
              <div class="col-5 col-xxl-7 custom-value">
                {{ factoryName }}
              </div>
            </div>
          </div>
          <div class="col-12 col-md-6">
            <div class="row py-1">
              <div class="col-6 col-xxl-3 custom-label">
                {{ lodash.upperFirst($t('products.singular')) }}
              </div>
              <div class="col-1 custom-label">
                :
              </div>
              <div class="col-5 col-xxl-8 custom-value">
                {{ productServiceNames }}
              </div>
            </div>
          </div>
          <div class="col-12">
            <div class="pb-1 pt-4">
              <b-table-simple class="resume-table" borderless outlined responsive>
                <b-thead>
                  <b-tr>
                    <b-th>No</b-th>
                    <b-th>{{ lodash.startCase($t('sale_notes.resumes.product_name')) }}</b-th>
                    <b-th>{{ `${$t('sale_notes.resumes.DoC')}` }}</b-th>
                    <b-th>{{ `${lodash.startCase($t('sale_notes.resumes.biomass'))} (${$t('units.kg')})` }}</b-th>
                    <b-th style="text-align: right">{{ `${lodash.startCase($t('sale_notes.resumes.price'))} (/${$t('units.kg')})` }}</b-th>
                    <b-th style="text-align: right">{{ lodash.startCase($t('sale_notes.resumes.amount')) }}</b-th>
                    <b-th>{{ lodash.startCase($t('sale_notes.resumes.fiber_label'))}}</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr v-for="(item, index) in tableItems" v-bind:key="index">
                    <b-td>{{ index + 1 }}</b-td>
                    <b-td>{{ item.name }}</b-td>
                    <b-td>{{ item.doc }}</b-td>
                    <b-td>{{ item.biomass }}</b-td>
                    <b-td style="text-align: right">{{ item.price }}</b-td>
                    <b-td style="text-align: right">{{ item.total_price }}</b-td>
                    <b-td>{{ item.label }}</b-td>
                  </b-tr>
                </b-tbody>
                <b-tfoot>
                  <b-tr>
                    <b-td></b-td>
                    <b-td></b-td>
                    <b-td></b-td>
                    <b-td>{{ lodash.startCase(`${decimalFormatter(totalBiomass)} ${$t('units.kg')}`) }}</b-td>
                    <b-td></b-td>
                    <b-td style="text-align: right">{{ currencyFormatter(totalPrice) }}</b-td>
                    <b-td>{{ lodash.startCase(`${decimalFormatter(totalBox > 0 ? totalBox : totalBoxFromDO)} ${$t('sale_notes.resumes.fiber')}`) }}</b-td>
                  </b-tr>
                </b-tfoot>
              </b-table-simple>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
export default {
  name: 'trip',
  props: {
    purchaseOrders: {type: Array, default: function () { return {} }},
    totalBox: {type: Number, default: 0},
    value: {type: Object, default: function () { return [] }},
  },
  computed: {
    factoryName() {
      return this.value?.customer?.name || '-'
    },
    trippedAt() {
      if (!this.value?.tripped_at) return '-'
      return this.$moment.utc(this.value.tripped_at).local().format('DD MMMM YYYY')
    },
    orderId() {
      return this.value?.delivery_order?.id || '-'
    },
    partnerNames() {
      let customerNames = this.purchaseOrders?.map(({ customer: { name }}) => name)
      if (!customerNames.length) return '-'
      return this.lodash.uniq(customerNames).join(', ')
    },
    productServiceNames() {
      let names = this.purchaseOrders?.map(({product: {name}}) => name)
      if (!names.length) return '-'
      return this.lodash.uniq(names).join(', ')
    },
    tableItems() {
      return this.value?.trip_items.map(({
        purchase_item: { quality, doc, unit_price: purchase_item_unit_price},
        size,
        quantity,
        label,
      }) => ({
        name: `Vannamei/${this.qualityFormatter(quality)}/Size ${size}`,
        doc: doc ?? '-',
        biomass: quantity,
        size: size,
        price: this.currencyFormatter(purchase_item_unit_price || 0),
        total_price: this.currencyFormatter(purchase_item_unit_price * quantity || 0),
        label,
      }))
    },
    totalBiomass () {
      return this.value?.trip_item_total_quantity || 0
    },
    totalBoxFromDO() {
      return this.value?.delivery_order?.delivery_item_total_packing || 0
    },
    totalPrice() {
      return this.value?.trip_items?.reduce((acc, {purchase_item: {unit_price}, quantity}) => acc + (unit_price * quantity), 0) || 0
    }
  },
  methods: {
    currencyFormatter(value) {
      return this.$numeral(value).format('$ 0,0.[00]')
    },
    decimalFormatter(value) {
      return this.$numeral(value).format('0,0.[00]')
    },
    qualityFormatter(quality) {
      return ['fresh', 'merah'].includes(quality) ? this.lodash.upperFirst(quality) : this.lodash.upperCase(quality)
    }
  }
}
</script>

<style lang="scss" scoped>

</style>